import { useCallback } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { env } from '@config/env';

/**
 * Read a LaunchDarkly flag value with a typed default.
 * Mirrors the contract of `/portal/src/app/hooks/useFeatureFlag().js`.
 * When the LD client isn't initialised (no client ID configured locally, or
 * the provider hasn't finished bootstrapping), this returns the supplied
 * default value so feature-gated UI degrades to "off" rather than throwing.
 * LD camelCases hyphen-separated flag keys. We do the same conversion to
 * keep call sites readable when source flags use kebab-case.
 */
function ldKey(flagKey: string): string {
  if (!flagKey.includes('-')) return flagKey;
  return flagKey
.split('-')
.map((part, i) =>
      i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
    )
.join('');
}

function resolveFlag<T>(
  client: unknown,
  flags: Record<string, unknown> | undefined,
  flagKey: string,
  defaultValue: T,
): T {
  if (!client || !flags) return defaultValue;
  const value = flags[ldKey(flagKey)];
  if (value === undefined || value === null) return defaultValue;
  return value as T;
}

export function useFeatureFlag<T>(flagKey: string, defaultValue: T): T {
  const client = useLDClient();
  const flags = useFlags() as Record<string, unknown> | undefined;
  return resolveFlag(client, flags, flagKey, defaultValue);
}

/**
 * Whether LaunchDarkly has finished initializing, so a flag value can be
 * trusted. Until the client streams its first payload, `useFeatureFlag` returns
 * the supplied default — which is fine for hiding UI, but dangerous for a route
 * gate whose fallback is a redirect: on a hard refresh the redirect would fire
 * before the real flag value arrives (the flag-flash redirect). Gates use this
 * to hold a loading state until the flag is trustworthy.
 *
 * When no client ID is configured, LD never boots and every flag resolves to
 * its default forever — so we report "ready" immediately rather than block.
 */
export function useFlagsReady(): boolean {
  const client = useLDClient();
  if (!env.VITE_LAUNCHDARKLY_CLIENT_ID) return true;
  return client != null;
}

/**
 * Callback form of `useFeatureFlag` for call sites that evaluate flags for a
 * list of items (e.g. the nav model), where one hook per item would break the
 * rules of hooks. Same resolution semantics as `useFeatureFlag`.
 */
export function useFlagChecker(): (flagKey: string, defaultValue: boolean) => boolean {
  const client = useLDClient();
  const flags = useFlags() as Record<string, unknown> | undefined;
  return useCallback(
    (flagKey: string, defaultValue: boolean) => resolveFlag(client, flags, flagKey, defaultValue),
    [client, flags],
  );
}
